import { useState, useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { useProfile } from "@/hooks/useProfile";
import { useGlobalExplainerToggles, ExplainerFlags } from "@/hooks/useExplainers";

// Session-level flag to prevent tour from re-showing during same browser session
// This prevents infinite loops and ensures tour only shows once per session
let tourShownThisSession = false;

export function useOnboardingTour() {
  const { user } = useAuth();
  const { data: profile, isLoading: profileLoading } = useProfile();
  const { data: globalToggles } = useGlobalExplainerToggles();
  const queryClient = useQueryClient();
  
  // Local state to track if tour should show (for manual triggers like "Take Tour Again")
  const [manualTourTrigger, setManualTourTrigger] = useState(false);

  // Cast profile to include new fields
  const flags = profile as unknown as ExplainerFlags | null;

  // Check if tour should show based on flags and global toggles
  // Use explicit boolean check to handle null values properly
  const shouldShowTour = !!user && !!profile && !profileLoading && (
    // Manual trigger from "Take Tour Again" button
    manualTourTrigger ||
    // New user who hasn't seen tour (handle null as false, so !false = true for null, check explicitly)
    (profile.has_seen_tour !== true && !tourShownThisSession) ||
    // Global force toggles for specific phases (admin feature)
    (globalToggles?.force_site_tour_core && flags?.site_tour_completed_core !== true && !tourShownThisSession) ||
    (globalToggles?.force_site_tour_role && flags?.site_tour_completed_role !== true && !tourShownThisSession)
  );

  const canResetTour = !!user && !!profile && !profileLoading;

  const markTourComplete = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error("Not authenticated");

      const { error } = await supabase
        .from("profiles")
        .update({ 
          has_seen_tour: true,
          site_tour_completed_core: true,
          site_tour_completed_role: true,
        })
        .eq("id", user.id);

      if (error) throw error;
      
      // Mark as shown this session to prevent re-showing
      tourShownThisSession = true;
    },
    onSuccess: () => {
      setManualTourTrigger(false);
      queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
  });

  const markCoreComplete = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error("Not authenticated");
      const { error } = await supabase
        .from("profiles")
        .update({ site_tour_completed_core: true })
        .eq("id", user.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
  });

  const markRoleComplete = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error("Not authenticated");
      const { error } = await supabase
        .from("profiles")
        .update({ site_tour_completed_role: true })
        .eq("id", user.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
  });

  const resetTour = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error("Not authenticated");

      const { error } = await supabase
        .from("profiles")
        .update({ 
          has_seen_tour: false,
          site_tour_completed_core: false,
          site_tour_completed_role: false,
        })
        .eq("id", user.id);

      if (error) throw error;
      
      // Reset session flag and set manual trigger
      tourShownThisSession = false;
    },
    onSuccess: () => {
      // Set manual trigger to ensure tour shows immediately
      setManualTourTrigger(true);
      queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
  });

  // Function to trigger tour manually (for "Take Tour Again" button)
  const triggerTour = useCallback(() => {
    tourShownThisSession = false;
    setManualTourTrigger(true);
  }, []);

  // Function to dismiss tour (when tour completes or is skipped)
  const dismissTour = useCallback(() => {
    tourShownThisSession = true;
    setManualTourTrigger(false);
  }, []);

  return {
    shouldShowTour,
    canResetTour,
    markTourComplete,
    markCoreComplete,
    markRoleComplete,
    resetTour,
    triggerTour,
    dismissTour,
    isLoading: profileLoading,
  };
}
